import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { Checkbox } from '@webapp/common/components/checkbox';
import { Scrollarea } from '@webapp/common/components/scrollarea';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { useToggle } from '@webapp/common/hooks/use-toggle';
import { setHtml, stopEvent } from '@webapp/common/lib/ui';

import { AgreementModal } from '@webapp/survey/src/components/questions/components/modal';
import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';

import css from './agreement.css';

const style = { maxWidth: '100%' };

const Label: FC<{
    agreementLeftLabel: string;
    agreementLink: string;
    agreementRightLabel: string;
    onToggle: (e: any) => void;
}> = ({ agreementLeftLabel, agreementLink, agreementRightLabel, onToggle }) => (
    <span className={CssUiComponent.LABEL}>
        <span {...setHtml(agreementLeftLabel)} />{' '}
        <a className={css.agreementLink} onClick={onToggle} {...setHtml(agreementLink)} />{' '}
        <span {...setHtml(agreementRightLabel)} />
    </span>
);

const Popup: FC<{
    agreementText: string;
    onClose: () => void;
}> = memo(({ agreementText, onClose }) => (
    <AgreementModal onClose={onClose}>
        <Scrollarea horizontal={false}>
            <div className={CssPageBlock.ANSWER} {...setHtml(agreementText)} />
        </Scrollarea>
    </AgreementModal>
));
Popup.displayName = 'Popup';

export const Agreement: VFC<SurveyQuestion> = ({
    actor,
    question: {
        id: questionId,
        params: { agreementLeftLabel, agreementLink, agreementRightLabel, agreementText },
        responseActor,
    },
}) => {
    const { enabled: modalVisible, off: close, toggle } = useToggle(false);
    const { value } = useSelector(responseActor, (r) => r.context);
    const togglePopup = useCallback(
        (e) => {
            stopEvent(e);
            toggle();
        },
        [toggle],
    );

    const label = useMemo(
        () => (
            <Label
                agreementLeftLabel={agreementLeftLabel}
                agreementLink={agreementLink}
                agreementRightLabel={agreementRightLabel}
                onToggle={togglePopup}
            />
        ),
        [agreementLeftLabel, agreementLink, agreementRightLabel, togglePopup],
    );

    const change = useCallback(
        (value) => {
            responseActor.send({ type: 'CHANGE', value, questionId });
        },
        [actor],
    );

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.AGREEMENT)} style={style}>
            {modalVisible && createPortal(<Popup agreementText={agreementText} onClose={close} />, document.body)}
            <Checkbox checked={value as boolean} label={label} onChange={change} />
        </div>
    );
};
Agreement.displayName = 'Agreement';
